import { useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Bell, Clock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { eventMetaFor } from '../eventMeta';
import { destinationFor } from '../destination';
import { groupNotificationsByDay } from '../groupNotificationsByDay';
import { ClientFacts } from './ClientFacts';
import { FactGrid } from './FactGrid';
import type { Fact } from './FactGrid';
import type { RefObject } from 'react';
import type { Notification } from '../types';
import { dayHeading, timeOfDay } from '@/features/Clients/activity';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface NotificationsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notifications: Array<Notification>;
  isLoading: boolean;
  isError: boolean;
  /**
   * The row that was clicked to get here — it opens selected. Undefined when
   * the panel was opened from the bell's footer, where no one item is the
   * subject and the newest is as good a default as any.
   */
  focusId?: string;
  /** Marks a row read the moment it is selected. See `PanelBody`. */
  onRead?: (id: string) => void;
  /** Marks every listed row read at once. */
  onReadAll?: (ids: Array<string>) => void;
  /**
   * Where focus goes when this closes.
   *
   * Radix restores focus to a Dialog's own `DialogTrigger`. There is none here
   * — the panel opens from a row inside a popover that has already unmounted —
   * so without this, `triggerRef` is null and focus falls to `<body>`.
   */
  returnFocusTo?: RefObject<HTMLButtonElement | null>;
}

/**
 * The activity feed as an inbox: a list on the left, one whole message on the
 * right.
 *
 * The frame is the PMS Figma's `NotificationPanel` — a wide two-pane reader
 * with an action in the bottom-right. What it replaced was a 512px single
 * column that showed the bell dropdown's rows again, only taller.
 *
 * THE READER PANE IS THE WHOLE PROBLEM, and the first build of it did not
 * solve it. The Figma filled the message from a `details` object — position
 * changes, report metrics, affected systems — that the API does not have:
 * `/v1/notifications` returns six strings per item and `subtitle` IS the
 * content. For the commonest event in a real org, `client_added`, that
 * subtitle is the client's NAME. So the pane rendered a screen-wide sheet of
 * white with two words in the corner, and the words were the same two already
 * visible in the list beside it.
 *
 * Three things fill it now, in order of how much they carry:
 *
 *   1. THE RECORD ITSELF, fetched. `client:<uuid>` carries an id that
 *      `getClient` takes, so a client notification shows the client — status,
 *      type, AUM, year-to-date return, advisor, onboarding, KYC due, contact.
 *      See `ClientFacts`, which also says why the other kinds do not get this.
 *   2. WHAT THE EVENT MEANS. One sentence of copy per event type, saying what
 *      happened and what there is to do about it. Twelve terse server titles
 *      ("Data gap reported") assume a reader who already knows the system.
 *   3. WHAT WE KNOW ABOUT THE EVENT — the absolute instant, the category, the
 *      read state — as facts rather than as a sentence.
 *
 * And the list beside it is a list again rather than twelve identical rows:
 * day headings, an unread filter, per-event-type icons (`eventMeta.tsx`), and
 * arrow-key navigation.
 *
 * NO "ARCHIVE", which the Figma draws. It has no backend at all — the feed is
 * synthesised on each request from backtests, clients and sessions, so there is
 * no row to mark. "Mark as read" is here, but as a bulk action in the list
 * header where it can actually do something; per-message it would only ever be
 * pressed after selecting the message had already done it.
 */
export const NotificationsPanel = ({
  open,
  onOpenChange,
  notifications,
  isLoading,
  isError,
  focusId,
  onRead,
  onReadAll,
  returnFocusTo,
}: NotificationsPanelProps) => {
  const { t } = useTranslation('app-shell');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        /* `sm:max-w-none` is load-bearing: the base DialogContent pins
           `sm:max-w-lg`, which wins over a plain `w-[70vw]` at every breakpoint
           this app is used at and silently gives back the 512px column this
           replaces. `max-w-[1280px]` stops the reader's line length running to
           900px on an ultrawide. `max-h-[88vh]` because the fixed height is
           taller than a laptop viewport with two browser toolbars open. */
        className="flex h-[660px] max-h-[88vh] w-[78vw] max-w-[1280px] gap-0 overflow-hidden p-0 sm:max-w-[1280px]"
        /* The close button lives in the list header, per the Figma, so the
           default absolutely-positioned one would be a second X floating over
           the message pane. */
        showCloseButton={false}
        onCloseAutoFocus={(event) => {
          const bell = returnFocusTo?.current;
          if (!bell) return;
          event.preventDefault();
          bell.focus();
        }}
      >
        {/* Radix requires both, and the panel says all of this on screen
            already — a visible copy would be a title above a title. */}
        <DialogTitle className="sr-only">
          {t('header.notifications.dialog_title')}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {t('header.notifications.dialog_description')}
        </DialogDescription>

        <PanelBody
          notifications={notifications}
          isLoading={isLoading}
          isError={isError}
          focusId={focusId}
          onRead={onRead}
          onReadAll={onReadAll}
        />
      </DialogContent>
    </Dialog>
  );
};

interface PanelBodyProps {
  notifications: Array<Notification>;
  isLoading: boolean;
  isError: boolean;
  focusId?: string;
  onRead?: (id: string) => void;
  onReadAll?: (ids: Array<string>) => void;
}

type Filter = 'all' | 'unread';

/**
 * Split out so the selection resets itself.
 *
 * Radix unmounts `DialogContent` when the dialog closes, so state declared in
 * here starts fresh on every open — which is what `focusId` needs. Held in the
 * outer component instead, `useState(focusId)` would keep the first row anyone
 * ever clicked and ignore the initialiser from then on, so the second and every
 * later open would land on the wrong message.
 */
const PanelBody = ({
  notifications,
  isLoading,
  isError,
  focusId,
  onRead,
  onReadAll,
}: PanelBodyProps) => {
  const { t } = useTranslation('app-shell');

  const [selectedId, setSelectedId] = useState<string | undefined>(focusId);
  const [filter, setFilter] = useState<Filter>('all');
  const listRef = useRef<HTMLDivElement>(null);

  // Derived rather than stored, so a refetch that drops the selected item —
  // the feed is a live query capped at twelve, items do fall off the end —
  // lands on the newest instead of on a blank pane.
  //
  // `.at(0)` and not `[0]`. Without `noUncheckedIndexedAccess`, index access
  // types an empty array's first element as `Notification`, so every guard
  // below reads to the type checker as dead code — and eslint's
  // `no-unnecessary-condition` duly reports it as such. Annotating the const
  // does not help either: TS narrows from the initialiser regardless of the
  // declared type. `.at()` returns `T | undefined` and is simply telling the
  // truth. The empty case is real — these hooks run before the
  // `length === 0` early return below.
  const selected =
    notifications.find((n) => n.id === selectedId) ?? notifications.at(0);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  /*
    The unread filter keeps the OPEN message listed even once it is read.

    Selecting a row marks it read, so under a naive `filter(n => !n.isRead)`
    every click would delete the row it just landed on and shuffle everything
    below it up by one — you would be reading a message whose place in the list
    had vanished underneath the cursor, and the next arrow-key press would move
    somewhere unrelated. Pinning the selection keeps the list still.
  */
  const listed = useMemo(
    () =>
      filter === 'all'
        ? notifications
        : notifications.filter((n) => !n.isRead || n.id === selected?.id),
    [notifications, filter, selected?.id],
  );

  const select = (notification: Notification) => {
    setSelectedId(notification.id);
    // Reading it is what marks it read, so only unread rows write.
    if (!notification.isRead) onRead?.(notification.id);
  };

  /**
   * Arrow keys walk the list, the way every mail client's does.
   *
   * Bound to the scroll container rather than to each row, so it works whether
   * focus is on a row or on the container itself, and moves through the LISTED
   * order — not the full feed — so a filtered list steps through what is on
   * screen. `preventDefault` because the container scrolls, and without it the
   * arrow both moves the selection and scrolls the box under it.
   */
  const onListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    if (listed.length === 0) return;
    event.preventDefault();

    const at = listed.findIndex((n) => n.id === selected?.id);
    const next =
      event.key === 'ArrowDown'
        ? Math.min(at + 1, listed.length - 1)
        : Math.max(at - 1, 0);
    // `at === -1` (the open message is filtered out) walks from the top.
    const target = listed.at(at === -1 ? 0 : next);
    if (!target) return;
    select(target);
    listRef.current
      ?.querySelector<HTMLButtonElement>(
        `[data-row-id="${CSS.escape(target.id)}"]`,
      )
      ?.focus();
  };

  if (isLoading) {
    return (
      <div
        className="flex w-full flex-col gap-3 p-6"
        data-testid="notifications-panel-loading"
      >
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  /*
    Error before empty, and never both: "No new notifications" on a failed read
    is a claim about the firm rather than about the request. AND only when
    nothing is cached — react-query keeps `data` through a failed refetch, so
    gating on `isError` alone would blank a panel holding twelve perfectly good
    messages the moment a background poll happened to fail behind it.
  */
  if (isError && notifications.length === 0) {
    return (
      <div className="flex w-full items-center justify-center p-6">
        <p className="text-center text-sm text-muted-foreground">
          {t('header.notifications.error')}
        </p>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-2 p-6">
        <Bell className="h-6 w-6 text-muted-foreground opacity-40" />
        <p className="text-sm text-muted-foreground">
          {t('header.notifications.empty')}
        </p>
      </div>
    );
  }

  const groups = groupNotificationsByDay(listed);

  return (
    <div className="flex h-full w-full min-h-0">
      {/* ================= Left: the list ================= */}
      <div className="flex w-[340px] shrink-0 flex-col border-r border-border bg-muted/20">
        <div className="flex shrink-0 items-center justify-between px-4 pt-4">
          <h3 className="text-sm font-semibold">
            {t('header.notifications.title')}
          </h3>
          <DialogClose asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
              <X className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">{t('header.notifications.close')}</span>
            </Button>
          </DialogClose>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-3">
          {/* A two-state filter, as a radiogroup rather than two buttons: it is
              one choice with two options, and a screen reader should hear
              "Unread, 2 of 2" rather than two unrelated toggles. */}
          <div
            role="radiogroup"
            aria-label={t('header.notifications.filter_label')}
            className="flex items-center gap-1 rounded-lg bg-muted p-0.5"
          >
            <FilterTab
              active={filter === 'all'}
              onSelect={() => setFilter('all')}
              label={t('header.notifications.filter_all')}
              count={notifications.length}
            />
            <FilterTab
              active={filter === 'unread'}
              onSelect={() => setFilter('unread')}
              label={t('header.notifications.filter_unread')}
              count={unreadCount}
            />
          </div>

          {unreadCount > 0 && onReadAll && (
            <button
              type="button"
              onClick={() =>
                onReadAll(
                  notifications.filter((n) => !n.isRead).map((n) => n.id),
                )
              }
              className="rounded-sm text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              {t('header.notifications.mark_all_read')}
            </button>
          )}
        </div>

        {/* The scroll container is the list itself, so the scrollable region is
            the thing carrying the list semantics. */}
        <div
          ref={listRef}
          className="min-h-0 flex-1 overflow-y-auto pb-4"
          onKeyDown={onListKeyDown}
        >
          {listed.length === 0 && (
            <p className="px-4 py-8 text-center text-xs text-muted-foreground">
              {t('header.notifications.all_read')}
            </p>
          )}

          {groups.map((group) => (
            <section key={group.heading}>
              {/* Sticky, so the day you are scrolled into stays named. */}
              <h4 className="sticky top-0 z-10 bg-muted/20 px-4 py-1.5 text-[10px] font-semibold tracking-wider text-muted-foreground uppercase backdrop-blur-sm">
                {group.heading}
              </h4>
              <ul>
                {group.items.map((notification) => (
                  <li key={notification.id}>
                    <Row
                      notification={notification}
                      selected={notification.id === selected?.id}
                      onSelect={() => select(notification)}
                      unreadLabel={t('header.notifications.unread')}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>

      {/* ================= Right: the message ================= */}
      {/* Guarded, though it cannot be nullish here: the `length === 0` return
          above is what rules it out, and control-flow analysis does not carry
          that across `.at(0)`. Rendering nothing beats a crash if the two ever
          drift apart. */}
      {selected && <Reader notification={selected} />}
    </div>
  );
};

/* ------------------------------------------------------------------ */

const FilterTab = ({
  active,
  onSelect,
  label,
  count,
}: {
  active: boolean;
  onSelect: () => void;
  label: string;
  count: number;
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={active}
    /* An explicit name, because the visible one cannot be fixed with markup.
       The accessible name is built by TRIMMING each text node and joining them
       with nothing, so the label and the count concatenate into "All2" — and a
       space is not a fix, whether it sits between the nodes (`{' '}`, dropped
       as its own whitespace-only node) or inside one (`{` ${count}`}`, trimmed
       off the front). Only a non-space character survives, and a comma reads
       better to a screen reader than the colon that trick would need. */
    aria-label={`${label}, ${count}`}
    onClick={onSelect}
    className={cn(
      'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
      'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
      active
        ? 'bg-background text-foreground shadow-sm'
        : 'text-muted-foreground hover:text-foreground',
    )}
  >
    {label}
    <span className="ml-1.5 tabular-nums opacity-60">{count}</span>
  </button>
);

const Row = ({
  notification,
  selected,
  onSelect,
  unreadLabel,
}: {
  notification: Notification;
  selected: boolean;
  onSelect: () => void;
  unreadLabel: string;
}) => {
  const { Icon, className, chipClassName } = eventMetaFor(
    notification.eventType,
    notification.type,
  );
  return (
    <button
      type="button"
      data-row-id={notification.id}
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      /* `bg-muted/50` for hover and a left rail plus a surface for selection,
         not the Figma's `bg-accent` / `bg-accent/50`. Measured against the
         row's DESCRIPTION text, which is `text-muted-foreground` and so the
         first thing to fail: `bg-accent` gives 4.01:1 light / 3.87:1 dark,
         under 4.5:1. The focus indicator is a real `outline` — not a ring (a
         box-shadow) and not a tint (a background), both of which forced-colors
         mode overrides. */
      className={cn(
        'flex w-full items-start gap-3 border-l-2 px-4 py-3 text-left transition-colors',
        'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
        selected
          ? 'border-l-primary bg-background'
          : 'border-l-transparent hover:bg-muted/50',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
          chipClassName,
        )}
        aria-hidden="true"
      >
        <Icon className={cn('h-3.5 w-3.5', className)} />
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex items-start gap-1.5">
          <span
            className={cn(
              'min-w-0 flex-1 text-[13px] leading-snug',
              notification.isRead ? 'font-medium' : 'font-semibold',
            )}
          >
            {/* Colon inside the string: a separate `{' '}` node is dropped
                from the computed accessible name, which ran the word into the
                title as one token ("UnreadNew client added"). */}
            {!notification.isRead && (
              <span className="sr-only">{`${unreadLabel}: `}</span>
            )}
            {notification.title}
          </span>
          {!notification.isRead && (
            <span
              className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              aria-hidden="true"
            />
          )}
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {notification.description}
        </span>
        <span className="mt-1 block text-[11px] text-muted-foreground/80">
          {notification.timestamp}
        </span>
      </span>
    </button>
  );
};

/* ------------------------------------------------------------------ */

const Reader = ({ notification }: { notification: Notification }) => {
  const { t } = useTranslation('app-shell');
  const navigate = useNavigate();
  const { Icon, className, chipClassName } = eventMetaFor(
    notification.eventType,
    notification.type,
  );
  const destination = destinationFor(notification);
  const clientId = notification.id.startsWith('client:')
    ? notification.id.slice('client:'.length)
    : null;

  const category = t(`header.notifications.types.${notification.eventType}`, {
    defaultValue: notification.eventType,
  });
  // Real product copy per event type, not derived from the payload. Absent for
  // a type nobody has written a line for yet, and then simply not rendered —
  // `defaultValue: ''` rather than the key, so a missing entry is silence
  // instead of "header.notifications.about.foo" printed at 14px.
  const about = t(`header.notifications.about.${notification.eventType}`, {
    defaultValue: '',
  });

  const facts: Array<Fact> = [
    { label: t('header.notifications.fact_event'), value: category },
    {
      label: t('header.notifications.fact_occurred'),
      value: `${dayHeading(notification.at)} · ${timeOfDay(notification.at)}`,
    },
    {
      label: t('header.notifications.fact_status'),
      value: notification.isRead
        ? t('header.notifications.status_read')
        : t('header.notifications.status_unread'),
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 border-b border-border px-6 py-5">
        <div className="flex items-start gap-3">
          <span
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
              chipClassName,
            )}
            aria-hidden="true"
          >
            <Icon className={cn('h-4 w-4', className)} />
          </span>
          <div className="min-w-0 flex-1">
            {/* `h2` and not the DialogTitle: the dialog is titled "Recent
                activity" for screen readers, and this is the heading of one
                message inside it. */}
            <h2 className="text-[17px] leading-tight font-semibold">
              {notification.title}
            </h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" aria-hidden="true" />
                {/* Absolute, unlike the list's frozen "4d ago" — which is
                    computed once at fetch and then never advances. */}
                <span>
                  {dayHeading(notification.at)} · {timeOfDay(notification.at)}
                </span>
              </span>
              <Badge variant="outline" className="h-5 text-[11px]">
                {category}
              </Badge>
              {!notification.isRead && (
                <Badge className="h-5 text-[11px]">
                  {t('header.notifications.unread_badge')}
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto px-6 py-5"
        tabIndex={0}
        role="region"
        aria-label={notification.title}
      >
        {/* PROSE IS CAPPED AT ~70 CHARACTERS. The pane is up to 940px wide, and
            an uncapped paragraph there runs to about 130 characters a line —
            past the point where the eye reliably finds the start of the next
            one. The facts grid below is deliberately NOT capped: those are
            short labelled values being scanned, not a line being read. */}
        <div className="max-w-[70ch] space-y-4">
          {/* The subtitle, whatever it is. No line-clamp anywhere below — that
              is the point of this pane. Short subtitles (a client's name) read
              as a subject line at this size; long ones (500 characters of a
              client's own words about the answers they dispute) read as the
              body. */}
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap">
            {notification.description}
          </p>

          {about && (
            <p className="text-sm leading-relaxed text-muted-foreground">
              {about}
            </p>
          )}
        </div>

        {/* A card, not a bare block under a rule. It groups the facts into one
            object the eye can take in as a unit, which the Figma's own
            `bg-muted/30 rounded-lg` details box did too — this is the same
            container filled with data that exists. */}
        <div className="mt-6 rounded-xl border border-border bg-muted/30 px-5 py-4">
          {clientId ? (
            <ClientFacts
              clientId={clientId}
              fallback={<FactGrid facts={facts} />}
            />
          ) : (
            <FactGrid facts={facts} />
          )}
        </div>
      </div>

      {destination && (
        <div className="flex shrink-0 justify-end border-t border-border px-6 py-4">
          {/* Wrapped in `DialogClose` rather than navigating alone. A modal that
              navigates underneath itself leaves its overlay sitting over the
              page it just moved to, and the only way out of that is Escape —
              which nobody presses, because the page they asked for is right
              there behind a grey sheet. */}
          <DialogClose asChild>
            <Button
              size="sm"
              onClick={() => {
                // Narrowed on the union member, not flattened to a string
                // pair: TanStack types `params` per route, and a
                // `Record<string, string>` would let a wrong param name
                // through the type checker.
                navigate(
                  'params' in destination
                    ? { to: destination.to, params: destination.params }
                    : { to: destination.to },
                );
              }}
            >
              {t(destination.labelKey)}
            </Button>
          </DialogClose>
        </div>
      )}
    </div>
  );
};
